import getBestBidColumn from "./getBestBidColumn";
import getNumberFromString from "./getNumber";


/*  
    This function will take set of unique bids
    as argument and sorts the bids according
    to the sorting order and filters the top
    bids
*/
function sortAndFilterTopBids(
    bids: Set<number>,
    sortInAscendingOrder: boolean,
    numberOfTopBidsToFilter: number
): Array<number> {
    const filteredTopBids = Array.from(bids).sort((a, b) => {
        if (sortInAscendingOrder) {
            return a - b;
        }else {
            return b - a;
        }
    }).slice(0, numberOfTopBidsToFilter);
    return filteredTopBids
}

/** Lot-level bid value: lot terms use lot_total_price; bid action 3 uses totals_row. */
function getSupplierLotTotalBidValue(supplierResponse, bestBidColumnId) {
    if (!supplierResponse) {
        return 0;
    }
    if (
        supplierResponse.lot_total_price !== undefined
        && supplierResponse.lot_total_price !== null
        && supplierResponse.lot_total_price !== ''
    ) {
        return getNumberFromString(supplierResponse.lot_total_price);
    }
    if (
        supplierResponse.totals_row
        && supplierResponse.totals_row[bestBidColumnId] !== undefined
        && supplierResponse.totals_row[bestBidColumnId] !== null
        && supplierResponse.totals_row[bestBidColumnId] !== ''
    ) {
        return getNumberFromString(supplierResponse.totals_row[bestBidColumnId]);
    }
    return 0;
}

function getSupplierTrpRawValue(supplierResponse) {
    if (!supplierResponse) {
        return undefined;
    }
    return (
        supplierResponse.transformation_price
        ?? supplierResponse.totals_row?.transformation_price
        ?? supplierResponse.totals_row?.tranformed_price
    );
}

function checkSortOrderIsAscending(eventInfo) {
    let sortInAscendingOrder = true;
    if (!eventInfo) {
        return sortInAscendingOrder;
    }
    const isSourcingSell = eventInfo.event_type === 'sourcing' && eventInfo.sourcing_type === 'sell';
    const isForwardAuction = eventInfo.event_type === 'auction' && eventInfo.auction_type === 'b';
    const isDutchAuctionWithDecreasingPrice = eventInfo.event_type === 'auction' && eventInfo.auction_type === 'c' && eventInfo.price_movement === 'decreases';
    if (
        isSourcingSell
        || isForwardAuction 
        || isDutchAuctionWithDecreasingPrice
    ) {
        sortInAscendingOrder = false;
    }
    return sortInAscendingOrder
}

function prepareLotLevelTopBids(
    supplierResponses,
    eventInfo,
    numberOfTopBidsToFilter,
    formatValue,
    bestBidColumnId,
    columnInfo
) {
    const bestBidColumnInfo = columnInfo[bestBidColumnId]
    const lotLevelUniqueBids = new Set();
    for (let supplier in supplierResponses) {
        const supplierResponse = supplierResponses[supplier]
        const lotTotalValue = getNumberFromString(supplierResponse.totals_row[bestBidColumnId])
        if (lotTotalValue > 0) {
            lotLevelUniqueBids.add(lotTotalValue);
        }
    }
    const sortInAscendingOrder = checkSortOrderIsAscending(eventInfo);
    const lotLevelTopBids = sortAndFilterTopBids(
        lotLevelUniqueBids,
        sortInAscendingOrder,
        numberOfTopBidsToFilter
    )
    const lotLevelFormattedTopBids = lotLevelTopBids.map(
        bidValue => formatValue(
            bidValue,
            bestBidColumnInfo.decimal_spaces
        )
    );
    return lotLevelFormattedTopBids
}

function prepareLineItemLevelTopBids(
    supplierResponses,
    eventInfo,
    numberOfTopBidsToFilter,
    formatValue,
    bestBidColumnId,
    columnInfo
) {
    const lineItemLevelUniqueBids = new Map()
    const bestBidColumnInfo = columnInfo[bestBidColumnId]
    // preparing the map which contains only the unique bids for each line item
    for (let supplier in supplierResponses) {
        const supplierResponse = supplierResponses[supplier];
        const rowData = supplierResponse.row_data
        for( let row in rowData) {
            const lineItemBidValue = getNumberFromString(rowData[row][bestBidColumnId])
            if (lineItemBidValue <= 0) {
                continue
            }
            if ( lineItemLevelUniqueBids.has(rowData[row].urid)) {
                const currentRowBids = lineItemLevelUniqueBids.get(rowData[row].urid);
                currentRowBids.add(lineItemBidValue)
            }else {
                lineItemLevelUniqueBids.set(rowData[row].urid, new Set([lineItemBidValue]))
            }
        }
    }

    const sortInAscendingOrder = checkSortOrderIsAscending(eventInfo);
    const lineItemLevelTopBids = new Map()
    // sorting and filtering the top bids of each line item
    for (const lineItemId of lineItemLevelUniqueBids.keys()) {

        let currentLineItemBids = lineItemLevelUniqueBids.get(lineItemId);
        const topBidsOfCurrentLineItem = sortAndFilterTopBids(
            currentLineItemBids,
            sortInAscendingOrder,
            numberOfTopBidsToFilter
        );

        // formatting the top bids of a line item
        const lineItemLevelFormattedTopBids = topBidsOfCurrentLineItem.map(
            bidValue => formatValue(
                bidValue,
                bestBidColumnInfo.decimal_spaces
            )
        );
        lineItemLevelTopBids.set(lineItemId, lineItemLevelFormattedTopBids)
    }
    return lineItemLevelTopBids;
}

function prepareTopBids(response, eventInfo, numberOfTopBidsToFilter, formatValue) {
    const bestBidColumnId = getBestBidColumn(response)
    const topBids = new Map();
    const lotLevelTopBids = prepareLotLevelTopBids(
        response.supplier_responses,
        eventInfo,
        numberOfTopBidsToFilter,
        formatValue,
        bestBidColumnId,
        response.column_info
    )
    topBids.set('lotLevelTopBids', lotLevelTopBids)
    if (!response.compute_transformed_cost
        || response.compute_transformed_cost == "no"
        || (
            response.compute_transformed_cost == "yes"
            && response.isTransformationAppliedAtLineItemLevel == true
        )
    ) {
        const lineItemLevelTopBids = prepareLineItemLevelTopBids(
            response.supplier_responses,
            eventInfo,
            numberOfTopBidsToFilter,
            formatValue,
            bestBidColumnId,
            response.column_info
        )
        topBids.set('lineItemLevelTopBids', lineItemLevelTopBids)
    }
    else {
        topBids.set('lineItemLevelTopBids', new Map())
    }
    return topBids
}

function updateBestBidsOfLineItems(response) {
    const lineItemLevelTopBids = response.bestBids.get('lineItemLevelTopBids')
    for (let row of response.items['row_data']){
        if (lineItemLevelTopBids.has(row.urid)) {
            row['best_bid'] = lineItemLevelTopBids.get(row.urid)[0]
        } else{
            row['best_bid'] = "-"
        }
    }
}
function calculatePercentageDifference(bestBid, bidValue, formatValue) {
    const absoluteDifference = Math.abs(bestBid - bidValue);
    const percentageDifference = (absoluteDifference / bestBid) * 100;
    return formatValue(percentageDifference, 2)
}

function updatePercentageDifferenceFromBestBid(response, formatValue) {
    const lineItemLevelTopBids = response.bestBids.get('lineItemLevelTopBids')
    const lotLevelTopBids = response.bestBids.get('lotLevelTopBids')
    const bestBidColumnId = getBestBidColumn(response)
    const bestBidOfCurrentLot = getNumberFromString(lotLevelTopBids[0])
    for (let sup in response.supplier_responses){
        if(
            response.supplier_responses[sup]['totals_row']
            && response.supplier_responses[sup]['totals_row'][bestBidColumnId]
        ){
            const bidValueOfCurrentSupplier = getNumberFromString(response.supplier_responses[sup]['totals_row'][bestBidColumnId])
            const percentageDifference = calculatePercentageDifference(
                bestBidOfCurrentLot,
                bidValueOfCurrentSupplier,
                formatValue
            )
            response.supplier_responses[sup]['totals_row']['percentage_away_from_best_bid'] = percentageDifference
        }
        if (
            response.supplier_responses[sup]['row_data']
            && Object.values(response.supplier_responses[sup]['row_data']).length > 0
        ){
            for (let lineItemId in response.supplier_responses[sup]['row_data']) {
                let lineItem = response.supplier_responses[sup]['row_data'][lineItemId]
                if (
                    lineItemLevelTopBids.has(lineItem.urid)
                    && lineItem[bestBidColumnId]
                ) {
                    const bestBidOfCurrentLineItem = getNumberFromString(lineItemLevelTopBids.get(lineItem.urid)[0])
                    const bidValueOfCurrentLineItem = getNumberFromString(lineItem[bestBidColumnId])
                    const percentageDifference = calculatePercentageDifference(
                        bestBidOfCurrentLineItem,
                        bidValueOfCurrentLineItem,
                        formatValue
                    )
                    lineItem['percentage_away_from_best_bid'] = percentageDifference
                } 
            }
        }
    }
}

function updateLotLevelPercentageDifferenceFromBestBid(
    supplierResponses,
    bestBidOfLot,
    bestBidColumnId,
    formatValue
){
    const bestBidOfCurrentLot = getNumberFromString(bestBidOfLot)
    for (let supplier in supplierResponses){
        const bidValueOfCurrentSupplier = getNumberFromString(supplierResponses[supplier]['totals_row'][bestBidColumnId])
        const percentageDifference = calculatePercentageDifference(
            bestBidOfCurrentLot,
            bidValueOfCurrentSupplier,
            formatValue
        )
        supplierResponses[supplier]['totals_row']['percentage_away_from_best_bid'] = percentageDifference
    }
}

function updateLotLevelFieldsPercentageDifferenceFromBestBid(
    supplierResponses,
    lotLevelFields,
    lotLevelFieldsBestBids,
    formatValue
) {
    if (!lotLevelFields || !Array.isArray(lotLevelFields) || !lotLevelFieldsBestBids) {
        return;
    }

    // For each price lot term, calculate percentage difference for each supplier
    lotLevelFields.forEach(field => {
        if (field.input_field_type !== 'Price') {
            return;
        }

        const bestBidForField = lotLevelFieldsBestBids.get(field.ucid);
        if (!bestBidForField) {
            return;
        }

        const bestBidValue = getNumberFromString(bestBidForField);
        
        for (let supplier in supplierResponses) {
            const supplierResponse = supplierResponses[supplier];
            let fieldValue = 0;
            
            // Check if supplier has response for this field
            if (supplierResponse.lot_field_responses && 
                supplierResponse.lot_field_responses[field.ucid] !== undefined &&
                supplierResponse.lot_field_responses[field.ucid] !== '') {
                fieldValue = getNumberFromString(supplierResponse.lot_field_responses[field.ucid]);
            } else {
                // Use baseline value if no supplier response
                fieldValue = getNumberFromString(field.baseline_value || field.value || '0');
            }
            
            if (fieldValue > 0 && bestBidValue > 0) {
                const percentageDifference = calculatePercentageDifference(
                    bestBidValue,
                    fieldValue,
                    formatValue
                );
                
                // Initialize lot_field_percentage_differences if it doesn't exist
                if (!supplierResponse.lot_field_percentage_differences) {
                    supplierResponse.lot_field_percentage_differences = {};
                }
                
                supplierResponse.lot_field_percentage_differences[field.ucid] = percentageDifference;
            }
        }
    });
}

function updateLSTLTPPercentageDifferenceFromBestBid(
    supplierResponses,
    lstLtpBestBids,
    formatValue,
    bestBidColumnId
) {
    if (!lstLtpBestBids) {
        return;
    }

    // Calculate percentage for LST (Lot Sub Total)
    const lstBestBid = lstLtpBestBids.get('lot_sub_total');
    if (lstBestBid) {
        const lstBestBidValue = getNumberFromString(lstBestBid);
        
        for (let supplier in supplierResponses) {
            const supplierResponse = supplierResponses[supplier];
            
            if (supplierResponse.lot_sub_total !== undefined && 
                supplierResponse.lot_sub_total !== null && 
                supplierResponse.lot_sub_total !== '') {
                
                const lstValue = getNumberFromString(supplierResponse.lot_sub_total);
                
                if (lstValue > 0 && lstBestBidValue > 0) {
                    const percentageDifference = calculatePercentageDifference(
                        lstBestBidValue,
                        lstValue,
                        formatValue
                    );
                    
                    // Initialize lst_ltp_percentage_differences if it doesn't exist
                    if (!supplierResponse.lst_ltp_percentage_differences) {
                        supplierResponse.lst_ltp_percentage_differences = {};
                    }
                    
                    supplierResponse.lst_ltp_percentage_differences['lot_sub_total'] = percentageDifference;
                }
            }
        }
    }

    // Calculate percentage for LTP (Lot Total Price)
    const ltpBestBid = lstLtpBestBids.get('lot_total_price');
    if (ltpBestBid) {
        const ltpBestBidValue = getNumberFromString(ltpBestBid);
        
        for (let supplier in supplierResponses) {
            const supplierResponse = supplierResponses[supplier];
            
            const ltpValue = getSupplierLotTotalBidValue(supplierResponse, bestBidColumnId);

            if (ltpValue > 0 && ltpBestBidValue > 0) {
                const percentageDifference = calculatePercentageDifference(
                    ltpBestBidValue,
                    ltpValue,
                    formatValue
                );

                if (!supplierResponse.lst_ltp_percentage_differences) {
                    supplierResponse.lst_ltp_percentage_differences = {};
                }

                supplierResponse.lst_ltp_percentage_differences['lot_total_price'] = percentageDifference;
            }
        }
    }

    // Percentage away from best bid for TRP (transformed price)
    const trpBestBid = lstLtpBestBids.get('transformation_price');
    if (trpBestBid) {
        const trpBestBidValue = getNumberFromString(trpBestBid);

        for (let supplier in supplierResponses) {
            const supplierResponse = supplierResponses[supplier];
            const rawTrp = getSupplierTrpRawValue(supplierResponse);
            if (rawTrp === undefined || rawTrp === null || rawTrp === '') {
                continue;
            }

            const trpValue = getNumberFromString(rawTrp);

            if (trpValue > 0 && trpBestBidValue > 0) {
                const percentageDifference = calculatePercentageDifference(
                    trpBestBidValue,
                    trpValue,
                    formatValue
                );

                if (!supplierResponse.lst_ltp_percentage_differences) {
                    supplierResponse.lst_ltp_percentage_differences = {};
                }

                supplierResponse.lst_ltp_percentage_differences['transformation_price'] = percentageDifference;
            }
        }
    }
}

function prepareLotLevelFieldsBestBids(
    supplierResponses,
    lotLevelFields,
    eventInfo,
    formatValue,
    bestBidColumnId,
    columnInfo
) {
    if (!lotLevelFields || !Array.isArray(lotLevelFields)) {
        return new Map();
    }

    const lotLevelFieldsBestBids = new Map();
    const bestBidColumnInfo = columnInfo[bestBidColumnId];
    const sortInAscendingOrder = checkSortOrderIsAscending(eventInfo);

    // For each price lot term, collect supplier responses and find best bid
    lotLevelFields.forEach(field => {
        if (field.input_field_type !== 'Price') {
            return;
        }

        const fieldValues = new Set();
        
        for (let supplier in supplierResponses) {
            const supplierResponse = supplierResponses[supplier];
            let fieldValue = 0;
            
            // Check if supplier has response for this field
            if (supplierResponse.lot_field_responses && 
                supplierResponse.lot_field_responses[field.ucid] !== undefined &&
                supplierResponse.lot_field_responses[field.ucid] !== '') {
                fieldValue = getNumberFromString(supplierResponse.lot_field_responses[field.ucid]);
            } else {
                // Use baseline value if no supplier response
                fieldValue = getNumberFromString(field.baseline_value || field.value || '0');
            }
            
            if (fieldValue > 0) {
                fieldValues.add(fieldValue);
            }
        }
        
        // Sort and get the best bid for this field
        const sortedValues = sortAndFilterTopBids(fieldValues, sortInAscendingOrder, 1);
        if (sortedValues.length > 0) {
            const bestBid = formatValue(sortedValues[0], bestBidColumnInfo.decimal_spaces);
            lotLevelFieldsBestBids.set(field.ucid, bestBid);
        }
    });

    return lotLevelFieldsBestBids;
}

function prepareLotLevelFieldsTopBids(
    supplierResponses,
    lotLevelFields,
    eventInfo,
    numberOfTopBidsToFilter,
    formatValue,
    bestBidColumnId,
    columnInfo
) {
    if (!lotLevelFields || !Array.isArray(lotLevelFields)) {
        return new Map();
    }

    const lotLevelFieldsTopBids = new Map();
    const bestBidColumnInfo = columnInfo[bestBidColumnId];
    const sortInAscendingOrder = checkSortOrderIsAscending(eventInfo);

    // For each price lot term, collect supplier responses and find top bids
    lotLevelFields.forEach(field => {
        if (field.input_field_type !== 'Price') {
            return;
        }

        const fieldValues = new Set();
        
        for (let supplier in supplierResponses) {
            const supplierResponse = supplierResponses[supplier];
            let fieldValue = 0;
            
            // Check if supplier has response for this field
            if (supplierResponse.lot_field_responses && 
                supplierResponse.lot_field_responses[field.ucid] !== undefined &&
                supplierResponse.lot_field_responses[field.ucid] !== '') {
                fieldValue = getNumberFromString(supplierResponse.lot_field_responses[field.ucid]);
            } else {
                // Use baseline value if no supplier response
                fieldValue = getNumberFromString(field.baseline_value || field.value || '0');
            }
            
            if (fieldValue > 0) {
                fieldValues.add(fieldValue);
            }
        }
        
        // Sort and get top bids for this field
        const topBidsOfCurrentField = sortAndFilterTopBids(
            fieldValues,
            sortInAscendingOrder,
            numberOfTopBidsToFilter
        );

        // Format the top bids of the lot level field
        const lotLevelFieldFormattedTopBids = topBidsOfCurrentField.map(
            bidValue => formatValue(
                bidValue,
                bestBidColumnInfo.decimal_spaces
            )
        );
        lotLevelFieldsTopBids.set(field.ucid, lotLevelFieldFormattedTopBids);
    });

    return lotLevelFieldsTopBids;
}

function prepareLSTLTPBestBids(
    supplierResponses,
    eventInfo,
    formatValue,
    bestBidColumnId,
    columnInfo
) {
    const bestBidColumnInfo = columnInfo[bestBidColumnId];
    const sortInAscendingOrder = checkSortOrderIsAscending(eventInfo);
    const lstLtpBestBids = new Map();

    // Collect LST (Lot Sub Total) values from all suppliers
    const lstValues = new Set();
    for (let supplier in supplierResponses) {
        const supplierResponse = supplierResponses[supplier];
        if (supplierResponse.lot_sub_total !== undefined && supplierResponse.lot_sub_total !== null && supplierResponse.lot_sub_total !== '') {
            const lstValue = getNumberFromString(supplierResponse.lot_sub_total);
            if (lstValue > 0) {
                lstValues.add(lstValue);
            }
        }
    }

    // Get best bid for LST
    const lstSortedValues = sortAndFilterTopBids(lstValues, sortInAscendingOrder, 1);
    if (lstSortedValues.length > 0) {
        const lstBestBid = formatValue(lstSortedValues[0], bestBidColumnInfo.decimal_spaces);
        lstLtpBestBids.set('lot_sub_total', lstBestBid);
    }

    // Collect LTP (Lot Total Price) values from all suppliers
    const ltpValues = new Set();
    for (let supplier in supplierResponses) {
        const supplierResponse = supplierResponses[supplier];
        const ltpValue = getSupplierLotTotalBidValue(supplierResponse, bestBidColumnId);
        if (ltpValue > 0) {
            ltpValues.add(ltpValue);
        }
    }

    // Get best bid for LTP
    const ltpSortedValues = sortAndFilterTopBids(ltpValues, sortInAscendingOrder, 1);
    if (ltpSortedValues.length > 0) {
        const ltpBestBid = formatValue(ltpSortedValues[0], bestBidColumnInfo.decimal_spaces);
        lstLtpBestBids.set('lot_total_price', ltpBestBid);
    }

    // Collect TRP (transformed / lot-level transformation total) values from all suppliers
    const trpValues = new Set();
    for (let supplier in supplierResponses) {
        const supplierResponse = supplierResponses[supplier];
        const rawTrp = getSupplierTrpRawValue(supplierResponse);
        if (rawTrp !== undefined && rawTrp !== null && rawTrp !== '') {
            const trpValue = getNumberFromString(rawTrp);
            if (trpValue > 0) {
                trpValues.add(trpValue);
            }
        }
    }

    const trpSortedValues = sortAndFilterTopBids(trpValues, sortInAscendingOrder, 1);
    if (trpSortedValues.length > 0) {
        const trpBestBid = formatValue(trpSortedValues[0], bestBidColumnInfo.decimal_spaces);
        lstLtpBestBids.set('transformation_price', trpBestBid);
    }

    return lstLtpBestBids;
}

export {
    checkSortOrderIsAscending,
    prepareTopBids,
    prepareLotLevelTopBids,
    prepareLotLevelFieldsBestBids,
    prepareLotLevelFieldsTopBids,
    prepareLSTLTPBestBids,
    updateBestBidsOfLineItems,
    updatePercentageDifferenceFromBestBid,
    updateLotLevelPercentageDifferenceFromBestBid,
    updateLotLevelFieldsPercentageDifferenceFromBestBid,
    updateLSTLTPPercentageDifferenceFromBestBid
};